import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import '../styles/Home.css';

interface Event {
  id: string;
  title: string;
  date: string;
  time: string;
  location: string;
  description: string;
  interestedUsers?: string[];
}

const Events: React.FC = () => {
  const { user, token } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = () => {
    fetch(`${API_URL}/api/events`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setEvents(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching events:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleDeleteEvent = (eventId: string) => {
    if (!token || !window.confirm('Are you sure you want to remove this event?')) return;

    fetch(`${API_URL}/events/${eventId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        alert('Event removed successfully.');
        fetchEvents();
      } else {
        alert(data.error || 'Failed to remove event.');
      }
    })
    .catch(err => alert('Error: ' + err.message));
  };

  const handleInterested = (eventId: string) => {
    if (!token) {
      alert('Please log in to show interest in events!');
      return;
    }

    fetch(`${API_URL}/api/events/${eventId}/interested`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        alert('🌟 You are now marked as interested!');
        fetchEvents();
      } else {
        alert(data.error || 'Failed to update interest.');
      }
    })
    .catch(err => alert('Error: ' + err.message));
  };

  if (loading) return <div className="home-container"><div className="loading-state">Loading events...</div></div>;

  return (
    <div className="home-container">
      <header className="hero" style={{ marginBottom: '40px' }}>
        <h1>Upcoming Events</h1>
        <p>Join us for community workshops, meetups, and fun activities across Nova Scotia!</p>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '800px', margin: '0 auto' }}>
        {events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f9f9f9', borderRadius: '20px' }}>
            <h3>No events scheduled at the moment.</h3>
            <p>Check back soon for workshops and community meetups!</p>
          </div>
        ) : (
          events.map(event => (
            <div key={event.id} style={{ 
              backgroundColor: 'white', 
              padding: '30px', 
              borderRadius: '20px', 
              boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
              borderLeft: '6px solid var(--primary-color)',
              textAlign: 'left'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>{event.title}</h2>
                <span style={{ 
                  backgroundColor: '#e3f2fd', 
                  color: '#1976d2', 
                  padding: '5px 12px', 
                  borderRadius: '20px', 
                  fontSize: '0.85rem', 
                  fontWeight: 'bold' 
                }}>
                  {event.date}
                </span>
              </div>
              <p style={{ color: '#666', marginBottom: '10px' }}><strong>🕒 Time:</strong> {event.time}</p>
              <p style={{ color: '#666', marginBottom: '10px' }}><strong>📍 Location:</strong> {event.location}</p>
              <p style={{ lineHeight: '1.6', color: '#444' }}>{event.description}</p>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '15px' }}>
                <button 
                  className="view-btn" 
                  onClick={() => handleInterested(event.id)}
                  disabled={event.interestedUsers?.includes(user?.email || '')}
                  style={{ 
                    opacity: event.interestedUsers?.includes(user?.email || '') ? 0.6 : 1,
                    cursor: event.interestedUsers?.includes(user?.email || '') ? 'default' : 'pointer'
                  }}
                >
                  {event.interestedUsers?.includes(user?.email || '') ? 'Interested!' : 'Interested'}
                </button>
                {event.interestedUsers && event.interestedUsers.length > 0 && (
                  <span style={{ fontSize: '0.9rem', color: '#666', fontWeight: 'bold' }}>
                    🌟 {event.interestedUsers.length} {event.interestedUsers.length === 1 ? 'person is' : 'people are'} interested
                  </span>
                )}
                {user?.id.startsWith('sitter-') && (
                  <button 
                    className="decline-btn" 
                    onClick={() => handleDeleteEvent(event.id)}
                    style={{ marginLeft: 'auto', padding: '8px 16px', fontSize: '0.8rem' }}
                  >
                    Remove Event
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Events;
